import {
  CalendarOutlineIcon,
  ChildrenSolidIcon,
  FilterOutlineIcon,
  TimerOutlineIcon,
} from '@ikyucom/design-system-icons-react'
import { ElementButton } from 'macaron-frontend-ui'
import { useCallback } from 'react'
import InventoryNotificationComponent from '~/features/guide/InventoryNotification'
import { SCENE_TAGS } from '~/lib/constants/constants'
import { format } from '~/lib/date/date'
import type { RestaurantId, RestaurantTagId } from '~/lib/graphql/scalars'
import type { GuideContext } from '~/navigation'
import { useReserveNavigate, useReserveNavigationState } from '~/navigation'

type Props = {
  restaurantId: RestaurantId
  reservable: boolean | undefined
  supportsInventoryNotification: boolean
}

export function FloatFooter({ restaurantId, reservable, supportsInventoryNotification }: Props) {
  const navigate = useReserveNavigate()
  const {
    context: { visitorsCount, visitDate, visitTime },
  } = useReserveNavigationState('GUIDE') ?? { context: {} }

  const guide = useReserveNavigationState('GUIDE')

  // 条件が全て指定されているかどうか
  const isAllConditionSpecified =
    visitorsCount !== undefined && visitDate !== undefined && visitTime !== undefined

  // TODO: プラン詳細ページの場合、プランが決まっているのでカレンダーふすまを開く
  const openCalendar = useCallback(() => {
    navigate({ type: 'openCalendarModal', openCalendarInitially: true, isFiltered: false })
  }, [navigate])

  const reserve = useCallback(() => {
    navigate({ type: 'reserve' })
  }, [navigate])

  if (reservable === undefined) {
    return null
  }

  if (!reservable) {
    // 予約不可
    return (
      <div className="sticky bottom-0 left-0 w-full bg-neutral-000 p-3 shadow-outline">
        <div className="flex items-center justify-between gap-x-2 max-xs:flex-col max-xs:gap-y-2">
          <p
            className="w-52 shrink-0 text-neutral-600 max-xs:flex
    max-xs:w-full max-xs:items-center max-xs:justify-center max-xs:gap-x-2"
          >
            現在、予約可能日はございません。
          </p>
          {supportsInventoryNotification ? (
            // 空席通知がサポートされている
            <InventoryNotificationComponent
              size="default"
              restaurantId={restaurantId}
              buttonClassName="w-full"
            />
          ) : (
            <ElementButton
              label={isAllConditionSpecified ? '予約へすすむ' : '空席確認・予約'}
              size="default"
              disabled={!reservable}
              onClick={reserve}
              withSpread={true}
              buttonClassName="py-4 px-2"
            />
          )}
        </div>
      </div>
    )
  }

  // 予約可
  return (
    <div className="sticky bottom-0 left-0 w-full bg-neutral-000 p-3 shadow-outline">
      <div className="space-y-3">
        <div className="flex w-full shrink-0 items-center justify-between gap-y-1">
          <div className="flex flex-1 flex-wrap items-center gap-x-1">
            <ChildrenSolidIcon width={13} height={13} className="text-marron-light" />
            <ConditionalText
              specifiedValue={visitorsCount ? `${visitorsCount}名` : ''}
              defaultText="未定"
            />
            <CalendarOutlineIcon width={13} height={13} className="text-marron-light" />
            <ConditionalText
              specifiedValue={visitDate ? `${format(visitDate, 'M月d日(E)')}` : ''}
              defaultText="未定"
            />
            <TimerOutlineIcon width={13} height={13} className="text-marron-light" />
            <ConditionalText specifiedValue={visitTime ? `${visitTime}` : ''} defaultText="未定" />
            <FilterCriteriaLabel count={countFilterCriteria(guide?.context)} />
          </div>
          <ElementButton onClick={openCalendar} label="変更" color="paypay" size="xSmall" />
        </div>
        <ElementButton
          label={isAllConditionSpecified ? '予約へすすむ' : '空席確認・予約'}
          size="large"
          disabled={!reservable}
          onClick={reserve}
          withSpread={true}
          buttonClassName="py-4 px-2"
        />
      </div>
    </div>
  )
}

function FilterCriteriaLabel({ count }: { count: number }) {
  if (count === 0) {
    return null
  }
  return (
    <div className="flex items-center gap-x-1">
      <FilterOutlineIcon className="text-marron-light" width={12} height={12} />
      <p className="flex items-center text-sm font-bold">絞り込み中({count})</p>
    </div>
  )
}

// 条件が指定されているかどうかによってテキストとスタイルを変更
function ConditionalText({
  specifiedValue,
  defaultText,
}: {
  specifiedValue: string
  defaultText: string
}) {
  const textToShow = specifiedValue || defaultText
  const textStyle = specifiedValue ? 'font-bold' : 'text-neutral-700'
  return <p className={textStyle}>{textToShow}</p>
}

function countFilterCriteria(ctx?: GuideContext) {
  if (ctx === undefined) {
    return 0
  }
  const filters = [
    ctx.buffet,
    ctx.smokingAllowed !== undefined,
    ctx.freeFlow,
    ctx.sceneTagIds?.includes('2941' as RestaurantTagId),
    ctx.sceneTagIds?.includes('2981' as RestaurantTagId),
    ctx.sceneTagIds?.includes(SCENE_TAGS.CHRISTMAS),
    ctx.windowSide,
    ctx.seatTypes?.includes('COUNTER'),
    ctx.seatTypes?.includes('PRIVATE_ROOM'),
    ctx.seatTypes?.includes('TERRACE'),
    ctx.seatTypes?.includes('SEMI_PRIVATE_ROOM'),
    ctx.seatTypes?.includes('TABLE'),
    ctx.childUses?.includes('GRADE_SCHOOLERS'),
    ctx.childUses?.includes('PRE_SCHOOLERS'),
    ctx.lowerBoundPrice !== undefined || ctx.upperBoundPrice !== undefined,
  ]
  return filters.filter((f) => f).length
}
if (import.meta.vitest) {
  const { it, expect, describe } = import.meta.vitest
  describe('countFilterCriteria', () => {
    it('context がない場合', () => {
      expect(countFilterCriteria(undefined)).toBe(0)
    })
    it('絞り込み条件が選択されている場合', () => {
      expect(
        countFilterCriteria({
          buffet: true,
          smokingAllowed: true,
          freeFlow: true,
          sceneTagIds: ['2941', '2981', '379'] as RestaurantTagId[],
          windowSide: true,
          seatTypes: ['COUNTER', 'PRIVATE_ROOM', 'TERRACE'],
          childUses: ['GRADE_SCHOOLERS', 'PRE_SCHOOLERS'],
          lowerBoundPrice: 1000,
          upperBoundPrice: 2000,
        } as GuideContext)
      ).toBe(13)
    })
  })
}
